import { forwardRef } from 'react';
import { FormattedDate } from 'react-intl';

import { t } from 'translations';
import { AuroraCheckIcon } from 'ui/components/Icons/AuroraCheck';
import { AuroraRadioButton } from 'ui/components/Inputs/RadioButton';
import { RadioGroupOptionProps } from 'ui/components/Inputs/RadioGroup';
import { AuroraTitle } from 'ui/styles/text';

import { BillingPeriod, Currency, RatePlan } from '../../types';
import { getMonetaryValue } from '../../utils/helpers';
import {
  periodToPrepaidTextKey,
  periodToProductBillingNameKey,
  periodToProductNameKey,
} from '../../utils/productLocalization';
import { InfoList, InfoListItem } from './InfoList';
import { ProductInfo, TopRow } from './Label';
import { YearlyBackground } from './YearlyBackground';
import { YearlyBadge } from './YearlyBadge';

interface YearlyRadioButtonProps extends Omit<RadioGroupOptionProps, 'labelText'> {
  ratePlan: RatePlan;
  isPrepaid: boolean;
  nextPaymentDate?: Date;
}

interface MonthlyRadioButtonProps extends Omit<RadioGroupOptionProps, 'labelText'> {
  ratePlan: RatePlan;
  isPrepaid: boolean;
  nextPaymentDate?: Date;
}

const BADGE_TEXT = t('membership_planselector_badge_most_convenient');

const getPriceText = ({
  currency,
  nextPaymentDate,
  isPrepaid,
  price,
}: {
  currency: Currency;
  nextPaymentDate?: Date;
  isPrepaid: boolean;
  price: string;
}) => {
  const zeroPrice = getMonetaryValue(0, currency);
  const nextPaymentDateComponent = <FormattedDate value={nextPaymentDate} format="default" />;

  return isPrepaid
    ? t('membership_hub_product_page_prepaid_plan_info', {
        prepaid_validto: nextPaymentDateComponent,
        prepaid_amount: zeroPrice,
      })
    : t('membership_hub_product_page_plan_info', {
        date: nextPaymentDateComponent,
        amount: price,
      });
};

const CheckIcon = () => <AuroraCheckIcon $strokeColor="white" />;

export const YearlyRadioButton = forwardRef<HTMLInputElement, YearlyRadioButtonProps>(
  ({ ratePlan, isPrepaid, nextPaymentDate, value, ...radioProps }, ref) => {
    const price = getMonetaryValue(ratePlan.amount, ratePlan.currency);
    const priceTextPrepaidActive = t(periodToPrepaidTextKey(BillingPeriod.Years), {
      price: price,
    });
    const productPlanName = t(periodToProductBillingNameKey(BillingPeriod.Years));
    const dataTestId = periodToProductNameKey(BillingPeriod.Years);
    const priceText = getPriceText({
      currency: ratePlan.currency,
      nextPaymentDate,
      isPrepaid,
      price: price,
    });

    return (
      <ProductInfo checked={radioProps.checked}>
        <YearlyBackground />
        <TopRow>
          <YearlyBadge>{BADGE_TEXT}</YearlyBadge>
          <AuroraRadioButton data-testid={dataTestId} value={BillingPeriod.Years} ref={ref} {...radioProps} />
        </TopRow>
        <AuroraTitle as="strong">{productPlanName}</AuroraTitle>
        <InfoList $show={radioProps.checked}>
          <InfoListItem>
            <CheckIcon />
            {priceText}
          </InfoListItem>
          <InfoListItem>
            <CheckIcon />
            {priceTextPrepaidActive}
          </InfoListItem>
        </InfoList>
      </ProductInfo>
    );
  },
);

export const MonthlyRadioButton = forwardRef<HTMLInputElement, MonthlyRadioButtonProps>(
  ({ ratePlan, isPrepaid, nextPaymentDate, value, ...radioProps }, ref) => {
    const price = getMonetaryValue(ratePlan.amount, ratePlan.currency);
    const priceTextPrepaidActive = t(periodToPrepaidTextKey(BillingPeriod.Months), {
      price: price,
    });
    const productPlanName = t(periodToProductBillingNameKey(BillingPeriod.Months));
    const dataTestId = periodToProductNameKey(BillingPeriod.Years);
    const priceText = getPriceText({
      currency: ratePlan.currency,
      nextPaymentDate,
      isPrepaid,
      price: price,
    });

    return (
      <ProductInfo checked={radioProps.checked}>
        <TopRow>
          <AuroraTitle as="strong">{productPlanName}</AuroraTitle>
          <AuroraRadioButton data-testid={dataTestId} value={BillingPeriod.Months} ref={ref} {...radioProps} />
        </TopRow>
        <InfoList $show={radioProps.checked}>
          <InfoListItem>
            <CheckIcon />
            {priceText}
          </InfoListItem>
          <InfoListItem>
            <CheckIcon />
            {priceTextPrepaidActive}
          </InfoListItem>
        </InfoList>
      </ProductInfo>
    );
  },
);
